import React, { useState, useEffect } from 'react';
import { AlertOctagon, Search, MapPin, UserX } from 'lucide-react';
import apiClient from '../../services/apiService';
import Skeleton from '../common/Skeleton';
import Badge from '../common/Badge';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import NoDataFound from '../common/NoDataFound';
import { usePermission } from '../../contexts/PermissionContext';

/**
 * Scheduled compliance that CANNOT BE ATTENDED.
 *
 * Read from the SHADOW CADENCE (SCH_fire_queue) — the Tier-1 queue of planned
 * occurrences across each rule's whole tenure. These are materialised rows with
 * real scheduled dates, not a forecast.
 *
 * The queue stores the accountable AUTHORITY and leaves the person unresolved,
 * because a cadence belongs to a seat and the holder is resolved later at
 * form-up. So "cannot be attended" is a plain group-by: for this occurrence's
 * seat and geography, does anybody hold that seat there today?
 *
 * Grouped to (rule x authority x geography) — the level at which the gap is
 * actually fixed. A two-year monthly rule over four locations is ~96 queue rows
 * all saying one thing; listing them individually buries the signal.
 *
 * Worse than an overdue obligation: an overdue item at least appears in the
 * Backlog, whereas an obligation that is never created appears nowhere.
 */

interface Row {
    rule_id: number;
    source_module: string;
    source_type: string;
    rule_status: string;
    category_name?: string | null;
    frequency?: string | null;
    location_id: number;
    location_name: string;
    location_type?: string | null;
    authority_id?: number | null;
    accountable_name?: string | null;
    fallback_id?: number | null;
    fallback_name?: string | null;
    scope_mode?: string | null;
    /** Queued occurrences behind this group, over the rule's whole tenure. */
    occurrences: number;
    first_due?: string | null;
    last_due?: string | null;
    /** Of those, how many are already in the past. */
    already_missed: number;
    reason: 'NO_FALLBACK_DEFINED' | 'BOTH_VACANT';
    obligation_name?: string | null;
    remedy: string;
}

interface Summary {
    groups: number;
    occurrences: number;
    already_missed: number;
    no_fallback_defined: number;
    both_vacant: number;
    rules_affected: number;
    locations_affected: number;
    authorities_affected: number;
    /** Total shadow-cadence rows. Zero means the queue was never built. */
    queue_total: number;
}

const REASON_LABEL: Record<string, string> = {
    NO_FALLBACK_DEFINED: 'No fallback defined',
    BOTH_VACANT: 'Both seats vacant here',
};

const ComplianceUnattended: React.FC = () => {
    const { allowed: canView, loading: permLoading } = usePermission('COM_COMPLIANCE_REGISTER', 'view');
    const [rows, setRows] = useState<Row[]>([]);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [reason, setReason] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        apiClient.get('/api/v1/com/dashboard/unattended-cadences')
            .then(res => {
                setRows(res.data?.data ?? []);
                setSummary(res.data?.summary ?? null);
            })
            .catch(() => setError('Failed to load unattended cadences.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { setPage(1); }, [search, reason, pageSize]);

    const filtered = rows.filter(r => {
        if (reason && r.reason !== reason) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (r.obligation_name || '').toLowerCase().includes(q)
            || r.location_name.toLowerCase().includes(q)
            || (r.accountable_name || '').toLowerCase().includes(q)
            || (r.fallback_name || '').toLowerCase().includes(q);
    });
    const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

    if (!permLoading && !canView) {
        return <NoDataFound title="Not authorised" message="You do not have access to 'COM_COMPLIANCE_REGISTER'." />;
    }

    return (
        <div className="netbody-card flex flex-col" style={{ flex: 1, minHeight: 0 }}>
            <div className="netbody-header">
                <label className="filter-page-size-group">
                    <span className="filter-page-size-label">Show</span>
                    <FormControl.Select
                        value={String(pageSize)}
                        onChange={e => setPageSize(parseInt(e.target.value, 10))}
                        className="filter-page-size"
                    >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                    </FormControl.Select>
                </label>
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Select
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        className="filter-status filter-status--wide"
                        data-testid="SEL_unattended_reason"
                    >
                        <option value="">All reasons</option>
                        <option value="NO_FALLBACK_DEFINED">No fallback defined</option>
                        <option value="BOTH_VACANT">Both seats vacant</option>
                    </FormControl.Select>
                    <FormControl.Input
                        type="text"
                        placeholder="Search obligation, location or seat…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_unattended_search"
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">

                    {/* What this screen is for — stated, because a healthy
                        result here is an EMPTY table, and an empty table with
                        no explanation reads as a broken screen. */}
                    {!loading && !error && (
                        <div className={`cu-banner${summary && summary.queue_total === 0 ? ' cu-banner--info' : ''}`}>
                            <AlertOctagon size={15} />
                            <div>
                                <strong>
                                    {!summary || summary.queue_total === 0
                                        ? 'The shadow cadence has not been built yet'
                                        : summary.groups > 0
                                            ? `${summary.occurrences} scheduled compliance${summary.occurrences === 1 ? '' : 's'} cannot be attended`
                                            : 'Every queued compliance has someone to attend it'}
                                </strong>
                                <span>
                                    {!summary || summary.queue_total === 0
                                        ? <>Nothing is queued for this entity, so there is nothing to check. Run the cadence rollout to build the shadow cadence first — this screen reads the queue, it does not forecast.</>
                                        : summary.groups > 0
                                            ? <>Grouped into {summary.groups} seat–location gap{summary.groups === 1 ? '' : 's'} across {summary.rules_affected} rule{summary.rules_affected === 1 ? '' : 's'}, {summary.authorities_affected} seat{summary.authorities_affected === 1 ? '' : 's'} and {summary.locations_affected} location{summary.locations_affected === 1 ? '' : 's'}. Nobody holds the accountable seat — or its fallback — at the location these occurrences are queued against, so no obligation will be raised and nothing will appear in the Backlog either.</>
                                            : <>All {summary.queue_total} queued occurrences resolve to a seat that somebody currently holds at that location.</>}
                                </span>
                            </div>
                            {summary && summary.groups > 0 && (
                                <div className="cu-banner-tally">
                                    <span><b>{summary.already_missed}</b> already past due</span>
                                    <span><b>{summary.no_fallback_defined}</b> no fallback</span>
                                    <span><b>{summary.both_vacant}</b> both vacant</span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Obligation / Source</th>
                                        <th className="table-list-th">Fires at</th>
                                        <th className="table-list-th">Accountable seat</th>
                                        <th className="table-list-th">Fallback seat</th>
                                        <th className="table-list-th">Queued occurrences</th>
                                        <th className="table-list-th table-list-cell--status">Why it fails</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'sm', 'sm', 'xs', 'pill']} />}
                                    {!loading && error && (
                                        <tr>
                                            <td colSpan={7} className="table-list-cell text-red-500 text-sm text-center p-4">{error}</td>
                                        </tr>
                                    )}
                                    {!loading && !error && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="table-list-cell">
                                                <NoDataFound
                                                    title={rows.length === 0 ? 'Nothing unattended' : 'No matches'}
                                                    message={rows.length === 0
                                                        ? 'Every queued occurrence in the shadow cadence resolves to a seat somebody currently holds.'
                                                        : 'No rows match the current filters.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && !error && paged.map((r, idx) => (
                                        <tr
                                            key={`${r.rule_id}-${r.location_id}`}
                                            className="table-list-row"
                                            style={r.reason === 'NO_FALLBACK_DEFINED'
                                                ? { background: '#fef2f2' } : { background: '#fffbeb' }}
                                        >
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title">
                                                    {r.obligation_name || `${r.source_type} #${r.rule_id}`}
                                                </span>
                                                <span className="table-list-cell-sub">
                                                    {r.source_module} · cadence rule {r.rule_id}
                                                    {r.category_name ? ` · ${r.category_name}` : ''}
                                                    {r.scope_mode === 'region_based' ? ' · class scope' : ''}
                                                </span>
                                                <span className="table-list-cell-sub cu-remedy">{r.remedy}</span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="cu-loc"><MapPin size={11} /> {r.location_name}</span>
                                                {r.location_type && <span className="table-list-cell-sub">{r.location_type}</span>}
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="cu-seat cu-seat--vacant">
                                                    <UserX size={11} /> {r.accountable_name || '—'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                {r.fallback_name
                                                    ? <span className="cu-seat cu-seat--vacant"><UserX size={11} /> {r.fallback_name}</span>
                                                    : <span className="cu-seat cu-seat--none">Not defined</span>}
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="cu-occ">{r.occurrences}</span>
                                                <span className="table-list-cell-sub">
                                                    {r.frequency || '—'}
                                                    {r.first_due && r.last_due
                                                        ? ` · ${String(r.first_due).slice(0, 10)} → ${String(r.last_due).slice(0, 10)}`
                                                        : ''}
                                                </span>
                                                {r.already_missed > 0 && (
                                                    <span className="table-list-cell-sub cu-missed">
                                                        {r.already_missed} already past due
                                                    </span>
                                                )}
                                            </td>
                                            <td className="table-list-cell table-list-cell--status">
                                                <Badge
                                                    variant={r.reason === 'NO_FALLBACK_DEFINED' ? 'danger' : 'warning'}
                                                    size="sm"
                                                >
                                                    {REASON_LABEL[r.reason]}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={filtered.length}
                            onPageChange={setPage}
                            recordLabel="seat-location gaps"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComplianceUnattended;
